/**
 * Audit Logs Component
 * Displays system-level API activity logs for debugging and customer support
 * Backend middleware captures all API requests with detailed information
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Activity,
  Search,
  Filter,
  RefreshCw,
  Calendar,
  User,
  Globe,
  Clock,
  AlertCircle,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Download,
  Eye,
  X,
  Server,
  Shield,
  CheckCircle,
  XCircle,
  Flag,
  BarChart3,
  TrendingUp,
  Loader2,
  Database,
  Cpu,
  FileText
} from 'lucide-react';
import { toast } from '../utils/toastUtils';

const API_BASE = process.env.REACT_APP_API_URL || '';

// Method colors
const METHOD_COLORS = {
  GET: { bg: 'bg-blue-500/20', text: 'text-blue-400' },
  POST: { bg: 'bg-green-500/20', text: 'text-green-400' },
  PUT: { bg: 'bg-yellow-500/20', text: 'text-yellow-400' },
  PATCH: { bg: 'bg-orange-500/20', text: 'text-orange-400' },
  DELETE: { bg: 'bg-red-500/20', text: 'text-red-400' },
  default: { bg: 'bg-gray-500/20', text: 'text-gray-400' }
};

// Category colors
const CATEGORY_COLORS = {
  auth: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', icon: Shield },
  scan: { bg: 'bg-green-500/20', text: 'text-green-400', icon: Activity },
  admin: { bg: 'bg-purple-500/20', text: 'text-purple-400', icon: Shield },
  billing: { bg: 'bg-blue-500/20', text: 'text-blue-400', icon: Database },
  data: { bg: 'bg-cyan-500/20', text: 'text-cyan-400', icon: FileText },
  config: { bg: 'bg-orange-500/20', text: 'text-orange-400', icon: Cpu },
  user: { bg: 'bg-pink-500/20', text: 'text-pink-400', icon: User },
  system: { bg: 'bg-gray-500/20', text: 'text-gray-400', icon: Server },
  ai: { bg: 'bg-indigo-500/20', text: 'text-indigo-400', icon: Cpu },
  general: { bg: 'bg-gray-500/20', text: 'text-gray-400', icon: Globe }
};

const AuditLogs = () => {
  // State
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(50);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [methodFilter, setMethodFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [successFilter, setSuccessFilter] = useState('');
  const [suspiciousOnly, setSuspiciousOnly] = useState(false);
  const [dateRange, setDateRange] = useState(7);
  const [showFilters, setShowFilters] = useState(false);

  // Categories and resource types from API
  const [categories, setCategories] = useState([]);
  const [resourceTypes, setResourceTypes] = useState([]);

  // Detail modal
  const [selectedLog, setSelectedLog] = useState(null);

  // Get auth token
  const getToken = () => localStorage.getItem('access_token') || localStorage.getItem('token');

  // Fetch logs
  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      if (searchTerm) params.append('search', searchTerm);
      if (methodFilter) params.append('method', methodFilter);
      if (categoryFilter) params.append('action_category', categoryFilter);
      if (successFilter !== '') params.append('success', successFilter);
      if (suspiciousOnly) params.append('is_suspicious', 'true');

      // Date range
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - dateRange);
      params.append('start_date', startDate.toISOString());

      const response = await fetch(`${API_BASE}/api/v1/audit/logs?${params}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });

      if (!response.ok) throw new Error('Failed to fetch logs');

      const data = await response.json();
      setLogs(data.logs || []);
      setTotal(data.total || 0);
      setTotalPages(data.pages || 1);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      toast.error('Erreur lors du chargement des logs');
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchTerm, methodFilter, categoryFilter, successFilter, suspiciousOnly, dateRange]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const response = await fetch(`${API_BASE}/api/v1/audit/stats?days=${dateRange}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });

      if (!response.ok) throw new Error('Failed to fetch stats');

      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching audit stats:', error);
    } finally {
      setStatsLoading(false);
    }
  }, [dateRange]);

  // Fetch categories
  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/v1/audit/categories`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Fetch resource types
  const fetchResourceTypes = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/v1/audit/resource-types`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setResourceTypes(data.resource_types || []);
      }
    } catch (error) {
      console.error('Error fetching resource types:', error);
    }
  };

  // Fetch log detail
  const fetchLogDetail = async (logId) => {
    try {
      const response = await fetch(`${API_BASE}/api/v1/audit/log/${logId}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedLog(data);
      }
    } catch (error) {
      console.error('Error fetching log detail:', error);
      toast.error('Erreur lors du chargement du detail');
    }
  };

  // Flag as suspicious
  const flagSuspicious = async (logId, suspicious) => {
    try {
      const response = await fetch(`${API_BASE}/api/v1/audit/flag/${logId}?suspicious=${suspicious}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        toast.success(suspicious ? 'Marque comme suspect' : 'Marque retire');
        fetchLogs();
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Export logs
  const exportLogs = async (format = 'csv') => {
    try {
      const params = new URLSearchParams({ format });
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - dateRange);
      params.append('start_date', startDate.toISOString());

      const response = await fetch(`${API_BASE}/api/v1/audit/export?${params}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });

      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit_logs_${new Date().toISOString().split('T')[0]}.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export successful');
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  };

  // Initial load
  useEffect(() => {
    fetchCategories();
    fetchResourceTypes();
  }, []);

  // Fetch data on filter change
  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [fetchLogs, fetchStats]);

  // Format timestamp
  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Get method style
  const getMethodStyle = (method) => METHOD_COLORS[method] || METHOD_COLORS.default;

  // Get category style
  const getCategoryStyle = (category) => CATEGORY_COLORS[category] || CATEGORY_COLORS.general;

  return (
    <div className="h-full flex flex-col space-y-4">
      {/* Stats Cards */}
      {!statsLoading && stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-brand-dark rounded-lg p-4 border border-gray-800">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Total Requests</p>
                <p className="text-2xl font-bold text-white">{stats.total_actions?.toLocaleString()}</p>
              </div>
              <Activity className="w-8 h-8 text-brand-green" />
            </div>
          </div>

          <div className="bg-brand-dark rounded-lg p-4 border border-gray-800">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Succes</p>
                <p className="text-2xl font-bold text-green-400">{stats.success_count?.toLocaleString()}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
          </div>

          <div className="bg-brand-dark rounded-lg p-4 border border-gray-800">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Erreurs</p>
                <p className="text-2xl font-bold text-red-400">{stats.error_count?.toLocaleString()}</p>
              </div>
              <XCircle className="w-8 h-8 text-red-400" />
            </div>
          </div>

          <div className="bg-brand-dark rounded-lg p-4 border border-gray-800">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Suspects</p>
                <p className="text-2xl font-bold text-yellow-400">{stats.suspicious_count?.toLocaleString()}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-yellow-400" />
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher dans les logs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-brand-dark border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-brand-green focus:outline-none"
            />
          </div>
        </div>

        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`px-4 py-2 rounded-lg flex items-center gap-2 ${showFilters ? 'bg-brand-green text-black' : 'bg-brand-dark text-white border border-gray-700'}`}
        >
          <Filter className="w-4 h-4" />
          Filtres
        </button>

        <select
          value={dateRange}
          onChange={(e) => setDateRange(Number(e.target.value))}
          className="px-4 py-2 bg-brand-dark border border-gray-700 rounded-lg text-white focus:border-brand-green focus:outline-none"
        >
          <option value={1}>24 heures</option>
          <option value={7}>7 jours</option>
          <option value={30}>30 jours</option>
          <option value={90}>90 jours</option>
        </select>

        <button
          onClick={() => fetchLogs()}
          className="p-2 bg-brand-dark border border-gray-700 rounded-lg text-gray-400 hover:text-white"
        >
          <RefreshCw className="w-4 h-4" />
        </button>

        <button
          onClick={() => exportLogs('csv')}
          className="px-4 py-2 bg-brand-dark border border-gray-700 rounded-lg text-white flex items-center gap-2 hover:border-brand-green"
        >
          <Download className="w-4 h-4" />
          Export CSV
        </button>
      </div>

      {/* Expanded Filters */}
      {showFilters && (
        <div className="bg-brand-dark rounded-lg p-4 border border-gray-800 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-gray-400 text-sm mb-1">Methode</label>
            <select
              value={methodFilter}
              onChange={(e) => setMethodFilter(e.target.value)}
              className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white"
            >
              <option value="">Toutes</option>
              <option value="GET">GET</option>
              <option value="POST">POST</option>
              <option value="PUT">PUT</option>
              <option value="PATCH">PATCH</option>
              <option value="DELETE">DELETE</option>
            </select>
          </div>

          <div>
            <label className="block text-gray-400 text-sm mb-1">Categorie</label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white"
            >
              <option value="">Toutes</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-gray-400 text-sm mb-1">Statut</label>
            <select
              value={successFilter}
              onChange={(e) => setSuccessFilter(e.target.value)}
              className="w-full px-3 py-2 bg-brand-black border border-gray-700 rounded-lg text-white"
            >
              <option value="">Tous</option>
              <option value="true">Succes</option>
              <option value="false">Erreur</option>
            </select>
          </div>

          <div className="flex items-end">
            <label className="flex items-center gap-2 text-white cursor-pointer">
              <input
                type="checkbox"
                checked={suspiciousOnly}
                onChange={(e) => setSuspiciousOnly(e.target.checked)}
                className="w-4 h-4 rounded bg-brand-black border-gray-700"
              />
              Suspects uniquement
            </label>
          </div>
        </div>
      )}

      {/* Logs Table */}
      <div className="flex-1 bg-brand-dark rounded-lg border border-gray-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-brand-black/50">
              <tr>
                <th className="px-4 py-3 text-left text-gray-400 text-sm font-medium">Timestamp</th>
                <th className="px-4 py-3 text-left text-gray-400 text-sm font-medium">Utilisateur</th>
                <th className="px-4 py-3 text-left text-gray-400 text-sm font-medium">Methode</th>
                <th className="px-4 py-3 text-left text-gray-400 text-sm font-medium">Endpoint</th>
                <th className="px-4 py-3 text-left text-gray-400 text-sm font-medium">Categorie</th>
                <th className="px-4 py-3 text-left text-gray-400 text-sm font-medium">Statut</th>
                <th className="px-4 py-3 text-left text-gray-400 text-sm font-medium">Duree</th>
                <th className="px-4 py-3 text-left text-gray-400 text-sm font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-brand-green" />
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                    No log found
                  </td>
                </tr>
              ) : (
                logs.map((log) => {
                  const methodStyle = getMethodStyle(log.method);
                  const catStyle = getCategoryStyle(log.action_category);
                  const CategoryIcon = catStyle.icon;

                  return (
                    <tr
                      key={log.id}
                      className={`hover:bg-brand-black/30 ${log.is_suspicious ? 'bg-yellow-500/5' : ''}`}
                    >
                      <td className="px-4 py-3 text-gray-300 text-sm whitespace-nowrap">
                        {formatTime(log.timestamp)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-500" />
                          <span className="text-white text-sm truncate max-w-[150px]">
                            {log.user_email || log.user_id?.slice(0, 8) || 'Anonymous'}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${methodStyle.bg} ${methodStyle.text}`}>
                          {log.method}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-gray-300 text-sm truncate block max-w-[250px]" title={log.endpoint}>
                          {log.endpoint}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className={`flex items-center gap-1 px-2 py-1 rounded ${catStyle.bg} w-fit`}>
                          <CategoryIcon className={`w-3 h-3 ${catStyle.text}`} />
                          <span className={`text-xs ${catStyle.text}`}>{log.action_category || 'general'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {log.success ? (
                            <CheckCircle className="w-4 h-4 text-green-400" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-400" />
                          )}
                          <span className={`text-sm ${log.success ? 'text-green-400' : 'text-red-400'}`}>
                            {log.response_status}
                          </span>
                          {log.is_suspicious && (
                            <AlertTriangle className="w-4 h-4 text-yellow-400" title="Suspect" />
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-sm">
                        {log.duration_ms ? `${log.duration_ms}ms` : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => fetchLogDetail(log.id)}
                            className="p-1 text-gray-400 hover:text-white"
                            title="Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => flagSuspicious(log.id, !log.is_suspicious)}
                            className={`p-1 ${log.is_suspicious ? 'text-yellow-400' : 'text-gray-400 hover:text-yellow-400'}`}
                            title={log.is_suspicious ? 'Retirer le flag' : 'Marquer suspect'}
                          >
                            <Flag className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-800 flex items-center justify-between">
            <span className="text-gray-400 text-sm">
              {total.toLocaleString()} logs au total
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 bg-brand-black rounded-lg text-gray-400 hover:text-white disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-white">
                Page {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-2 bg-brand-black rounded-lg text-gray-400 hover:text-white disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedLog && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-brand-dark rounded-xl border border-gray-800 max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-800 flex items-center justify-between">
              <h3 className="text-lg font-bold text-white">Detail du Log</h3>
              <button
                onClick={() => setSelectedLog(null)}
                className="p-2 text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[60vh] space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-gray-500 text-sm">Timestamp</label>
                  <p className="text-white">{formatTime(selectedLog.timestamp)}</p>
                </div>
                <div>
                  <label className="text-gray-500 text-sm">Utilisateur</label>
                  <p className="text-white">{selectedLog.user_email || selectedLog.user_name || 'Anonymous'}</p>
                </div>
                <div>
                  <label className="text-gray-500 text-sm">Action</label>
                  <p className="text-white">{selectedLog.action}</p>
                </div>
                <div>
                  <label className="text-gray-500 text-sm">Categorie</label>
                  <p className="text-white">{selectedLog.action_category}</p>
                </div>
                <div>
                  <label className="text-gray-500 text-sm">Resource Type</label>
                  <p className="text-white">{selectedLog.resource_type}</p>
                </div>
                <div>
                  <label className="text-gray-500 text-sm">Statut</label>
                  <p className={selectedLog.success ? 'text-green-400' : 'text-red-400'}>
                    {selectedLog.response_status} - {selectedLog.success ? 'Succes' : 'Erreur'}
                  </p>
                </div>
                <div>
                  <label className="text-gray-500 text-sm">IP Address</label>
                  <p className="text-white">{selectedLog.ip_address || '-'}</p>
                </div>
                <div>
                  <label className="text-gray-500 text-sm">Duree</label>
                  <p className="text-white">{selectedLog.duration_ms ? `${selectedLog.duration_ms}ms` : '-'}</p>
                </div>
              </div>

              {selectedLog.user_agent && (
                <div>
                  <label className="text-gray-500 text-sm">User Agent</label>
                  <p className="text-white text-sm break-all">{selectedLog.user_agent}</p>
                </div>
              )}

              {selectedLog.error_message && (
                <div>
                  <label className="text-gray-500 text-sm">Message d'erreur</label>
                  <p className="text-red-400 text-sm">{selectedLog.error_message}</p>
                </div>
              )}

              {selectedLog.request_data && (
                <div>
                  <label className="text-gray-500 text-sm">Request Data</label>
                  <pre className="text-white text-sm bg-brand-black rounded-lg p-3 overflow-x-auto">
                    {JSON.stringify(selectedLog.request_data, null, 2)}
                  </pre>
                </div>
              )}

              {selectedLog.response_data && (
                <div>
                  <label className="text-gray-500 text-sm">Response Data</label>
                  <pre className="text-white text-sm bg-brand-black rounded-lg p-3 overflow-x-auto">
                    {JSON.stringify(selectedLog.response_data, null, 2)}
                  </pre>
                </div>
              )}

              <div className="flex items-center gap-4 pt-4 border-t border-gray-800">
                {selectedLog.is_sensitive && (
                  <span className="px-3 py-1 bg-purple-500/20 text-purple-400 rounded-full text-sm flex items-center gap-1">
                    <Shield className="w-4 h-4" />
                    Sensitive
                  </span>
                )}
                {selectedLog.is_suspicious && (
                  <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-sm flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" />
                    Suspect
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLogs;
